import { useState } from 'react'
import { Box, Stack, Table, Text, Badge, Anchor, Group, Select, Pagination, Skeleton, Checkbox, Button, Paper, UnstyledButton } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconRefresh } from '@tabler/icons-react'
import { useJiraIssues, useJiraConfig, useJiraUserMappings, useJiraSummary } from '@/hooks/useJiraData'
import { jiraApi } from '@/api/jira'
import { useCurrentTenant } from '@/store/auth'
import { WidgetCard, CardHeader } from '@/components/ui'
import { formatTimeAgo } from '@/utils/formatTimeAgo'
import { JiraIssueDetailDrawer } from './JiraIssueDetailDrawer'

const BULK_OPERATION_OPTIONS = [
  { value: 'sync', label: 'Sincronizar' },
  { value: 'force_push', label: 'Forçar envio ao Jira' },
  { value: 'force_pull', label: 'Forçar recebimento do Jira' },
]

export function JiraIssueList() {
  const tenant = useCurrentTenant()
  const tenantId = tenant?.id

  const [status, setStatus] = useState<string | null>(null)
  const [priority, setPriority] = useState<string | null>(null)
  const [assignee, setAssignee] = useState<string | null>(null)
  const [issueType, setIssueType] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const pageSize = 20

  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  const [bulkOperation, setBulkOperation] = useState<string | null>('sync')
  const [bulkRunning, setBulkRunning] = useState(false)

  const [drawerKey, setDrawerKey] = useState<string | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const { data: config } = useJiraConfig()
  const { data: summary } = useJiraSummary()
  const { data: userMappings } = useJiraUserMappings()
  const { data, isLoading } = useJiraIssues({
    status: status ?? undefined,
    priority: priority ?? undefined,
    assignee: assignee ?? undefined,
    issue_type: issueType ?? undefined,
    page,
    page_size: pageSize,
  })

  const catColors: Record<string, string> = {
    todo: 'blue',
    in_progress: 'yellow',
    done: 'green',
  }

  // Build filter options from summary data
  const statusOptions = (summary?.by_status ?? []).map(s => ({ value: s.name, label: `${s.name} (${s.count})` }))
  const priorityOptions = [
    { value: 'Low', label: 'Low' },
    { value: 'Medium', label: 'Medium' },
    { value: 'High', label: 'High' },
    { value: 'Highest', label: 'Highest' },
  ]
  const assigneeOptions = (userMappings ?? []).map(m => ({
    value: m.jira_account_id ?? m.jira_display_name ?? '',
    label: m.jira_display_name ?? m.jira_account_id ?? '',
  })).filter(o => o.value)
  const issueTypeOptions = [
    { value: 'Tarefa', label: 'Tarefa' },
    { value: 'Epic', label: 'Epic' },
    { value: 'História', label: 'História' },
    { value: 'Erro', label: 'Erro' },
    { value: 'Subtarefa', label: 'Subtarefa' },
  ]

  // Resolve assignee account ID to display name
  const resolveAssignee = (accountId: string) => {
    if (!accountId || !userMappings) return accountId || '—'
    const mapping = userMappings.find(m => m.jira_account_id === accountId)
    return mapping?.jira_display_name ?? accountId.slice(0, 12) + '...'
  }

  const toggleSelectKey = (key: string) => {
    setSelectedKeys(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const currentPageKeys = (data?.items ?? []).map(i => i.jira_issue_key)
  const allCurrentSelected = currentPageKeys.length > 0 && currentPageKeys.every(k => selectedKeys.has(k))

  const toggleSelectAll = () => {
    if (allCurrentSelected) {
      setSelectedKeys(prev => {
        const next = new Set(prev)
        currentPageKeys.forEach(k => next.delete(k))
        return next
      })
    } else {
      setSelectedKeys(prev => {
        const next = new Set(prev)
        currentPageKeys.forEach(k => next.add(k))
        return next
      })
    }
  }

  const handleBulkExecute = async () => {
    if (!tenantId || !bulkOperation || selectedKeys.size === 0) return
    setBulkRunning(true)
    try {
      const result = await jiraApi.bulkSync(tenantId, {
        operation: bulkOperation,
        issue_keys: Array.from(selectedKeys),
      })
      notifications.show({
        title: 'Ação em lote concluída',
        message: `${result.queued} enfileiradas, ${result.skipped} ignoradas${result.errors.length > 0 ? `, ${result.errors.length} erros` : ''}`,
        color: result.errors.length > 0 ? 'yellow' : 'green',
      })
      setSelectedKeys(new Set())
    } catch {
      notifications.show({ title: 'Erro', message: 'Falha ao executar ação em lote', color: 'red' })
    }
    setBulkRunning(false)
  }

  const totalPages = Math.ceil((data?.total ?? 0) / pageSize)

  return (
    <WidgetCard accent="none" p={0}>
      <CardHeader title="Issues Jira" />
      <Box p="md">
        <Stack gap="md">
          {/* Filters */}
          <Group gap="sm">
            <Select
              placeholder="Status"
              data={statusOptions}
              value={status}
              onChange={(v) => { setStatus(v); setPage(1) }}
              clearable
              size="xs"
              w={200}
            />
            <Select
              placeholder="Prioridade"
              data={priorityOptions}
              value={priority}
              onChange={(v) => { setPriority(v); setPage(1) }}
              clearable
              size="xs"
              w={150}
            />
            <Select
              placeholder="Responsável"
              data={assigneeOptions}
              value={assignee}
              onChange={(v) => { setAssignee(v); setPage(1) }}
              clearable
              size="xs"
              w={180}
              searchable
            />
            <Select
              placeholder="Tipo"
              data={issueTypeOptions}
              value={issueType}
              onChange={(v) => { setIssueType(v); setPage(1) }}
              clearable
              size="xs"
              w={140}
            />
          </Group>

          {/* Bulk Actions Toolbar */}
          {selectedKeys.size > 0 && (
            <Paper p="xs" withBorder bg="blue.0">
              <Group gap="sm">
                <Text size="xs" fw={500}>{selectedKeys.size} issue(s) selecionada(s)</Text>
                <Select
                  size="xs"
                  data={BULK_OPERATION_OPTIONS}
                  value={bulkOperation}
                  onChange={setBulkOperation}
                  w={200}
                />
                <Button
                  size="xs"
                  onClick={handleBulkExecute}
                  loading={bulkRunning}
                  disabled={!bulkOperation}
                  leftSection={<IconRefresh size={14} />}
                >
                  Executar
                </Button>
                <Button
                  size="xs"
                  variant="subtle"
                  color="gray"
                  onClick={() => setSelectedKeys(new Set())}
                >
                  Limpar seleção
                </Button>
              </Group>
            </Paper>
          )}

          {/* Table */}
          {isLoading ? (
            <Skeleton height={300} />
          ) : (
            <Table verticalSpacing="xs" horizontalSpacing="sm" fz="sm" striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={36}>
                    <Checkbox
                      size="xs"
                      checked={allCurrentSelected}
                      indeterminate={selectedKeys.size > 0 && !allCurrentSelected}
                      onChange={toggleSelectAll}
                    />
                  </Table.Th>
                  <Table.Th w={80}>Key</Table.Th>
                  <Table.Th>Summary</Table.Th>
                  <Table.Th w={160}>Status</Table.Th>
                  <Table.Th w={80}>Priority</Table.Th>
                  <Table.Th w={120}>Assignee</Table.Th>
                  <Table.Th w={60}>Updated</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(data?.items ?? []).map((issue) => (
                  <Table.Tr key={issue.jira_issue_key}>
                    <Table.Td>
                      <Checkbox
                        size="xs"
                        checked={selectedKeys.has(issue.jira_issue_key)}
                        onChange={() => toggleSelectKey(issue.jira_issue_key)}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        <UnstyledButton
                          onClick={() => { setDrawerKey(issue.jira_issue_key); setDrawerOpen(true) }}
                          style={{ fontWeight: 600, fontSize: 'var(--mantine-font-size-sm)', color: 'var(--mantine-color-anchor)' }}
                        >
                          {issue.jira_issue_key}
                        </UnstyledButton>
                        <Anchor
                          href={config ? `${config.jira_base_url}/browse/${issue.jira_issue_key}` : '#'}
                          target="_blank"
                          size="xs"
                          c="dimmed"
                          title="Abrir no Jira"
                        >
                          ↗
                        </Anchor>
                        {issue.consoleai_ticket_id && (
                          <Badge size="xs" variant="dot" color="teal">Vinculado</Badge>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" lineClamp={1}>{issue.summary}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="sm" color={catColors[issue.status_category] ?? 'gray'} variant="light">
                        {issue.status_name}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{issue.priority_name || '—'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" lineClamp={1}>{resolveAssignee(issue.assignee_jira_account_id)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">{formatTimeAgo(issue.jira_updated_at)}</Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
                {(data?.items ?? []).length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={7}>
                      <Text ta="center" c="dimmed" py="xl">Nenhuma issue encontrada</Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Group justify="space-between">
              <Text size="xs" c="dimmed">
                {data?.total ?? 0} issues • Página {page}/{totalPages}
              </Text>
              <Pagination total={totalPages} value={page} onChange={setPage} size="sm" />
            </Group>
          )}
        </Stack>
      </Box>

      <JiraIssueDetailDrawer
        jiraKey={drawerKey}
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />
    </WidgetCard>
  )
}
